import React from 'react';

type CaseAttachmentViewerAttachment = {
  url: string;
  fileName?: string | null;
  fileType?: string | null;
  mimeType?: string | null;
  fileSize?: number | string | null;
};

type CaseAttachmentViewerModalProps = {
  attachment: CaseAttachmentViewerAttachment | null;
  onClose: () => void;
  formatFileSize?: (value: any) => string;
};

const BRAND_TEXT = '#123F43';
const BRAND_MUTED = '#64716F';
const BRAND_BORDER = '#DDD4C8';
const BRAND_CARD = '#FFFDF9';
const BRAND_PANEL = '#FBF8F2';

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    zIndex: 2600
  },
  modal: {
    width: 'min(1120px, 96vw)',
    height: 'min(860px, 92vh)',
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    boxShadow: '0 28px 90px -36px rgba(15, 23, 42, 0.55)',
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    overflow: 'hidden'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    padding: '1rem 1.15rem',
    borderBottom: `1px solid ${BRAND_BORDER}`
  },
  titleBlock: {
    minWidth: 0,
    display: 'grid',
    gap: '0.2rem'
  },
  title: {
    margin: 0,
    color: BRAND_TEXT,
    fontSize: '1.25rem',
    fontWeight: 800,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  meta: {
    color: BRAND_MUTED,
    fontSize: '0.85rem'
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.55rem',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },
  utilityButton: {
    border: `1px solid ${BRAND_BORDER}`,
    backgroundColor: BRAND_CARD,
    color: BRAND_TEXT,
    borderRadius: '0.65rem',
    padding: '0.42rem 0.8rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    textDecoration: 'none'
  },
  primaryButton: {
    border: `1px solid ${BRAND_TEXT}`,
    backgroundColor: BRAND_TEXT,
    color: '#ffffff',
    borderRadius: '0.65rem',
    padding: '0.42rem 0.8rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer'
  },
  body: {
    minHeight: 0,
    backgroundColor: BRAND_PANEL,
    padding: '1rem',
    overflow: 'auto'
  },
  frame: {
    width: '100%',
    height: '100%',
    minHeight: '62vh',
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    backgroundColor: '#ffffff'
  },
  imageWrap: {
    minHeight: '62vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    backgroundColor: '#ffffff',
    padding: '1rem'
  },
  image: {
    maxWidth: '100%',
    maxHeight: '72vh',
    objectFit: 'contain'
  },
  unsupported: {
    minHeight: '38vh',
    display: 'grid',
    alignContent: 'center',
    justifyItems: 'center',
    gap: '0.75rem',
    border: `1px dashed ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    backgroundColor: '#ffffff',
    color: BRAND_MUTED,
    textAlign: 'center',
    padding: '2rem'
  }
};

function getPreviewKind(attachment: CaseAttachmentViewerAttachment) {
  const mime = String(attachment.mimeType || attachment.fileType || '').trim().toLowerCase();
  const fileName = String(attachment.fileName || '').trim().toLowerCase();

  if (mime.includes('pdf') || fileName.endsWith('.pdf')) return 'pdf';
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(fileName)) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'unsupported';
}

export function CaseAttachmentViewerModal({
  attachment,
  onClose,
  formatFileSize
}: CaseAttachmentViewerModalProps) {
  if (!attachment?.url) return null;

  const fileName = String(attachment.fileName || 'Attachment').trim() || 'Attachment';
  const fileMeta = [
    attachment.mimeType || attachment.fileType || 'File',
    formatFileSize ? formatFileSize(attachment.fileSize) : ''
  ].filter(Boolean).join(' | ');
  const previewKind = getPreviewKind(attachment);

  return (
    <div style={styles.backdrop} onClick={event => event.stopPropagation()}>
      <div
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={`Attachment viewer: ${fileName}`}
        onClick={event => event.stopPropagation()}
      >
        <div style={styles.toolbar}>
          <div style={styles.titleBlock}>
            <h2 style={styles.title} title={fileName}>{fileName}</h2>
            <div style={styles.meta}>{fileMeta || 'Case attachment'}</div>
          </div>
          <div style={styles.actions}>
            <a
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.utilityButton}
            >
              Open in tab
            </a>
            <a
              href={attachment.url}
              download={fileName}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.utilityButton}
            >
              Download
            </a>
            <button type="button" style={styles.primaryButton} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <div style={styles.body}>
          {previewKind === 'pdf' ? (
            <iframe title={fileName} src={attachment.url} style={styles.frame} />
          ) : previewKind === 'image' ? (
            <div style={styles.imageWrap}>
              <img src={attachment.url} alt={fileName} style={styles.image} />
            </div>
          ) : previewKind === 'video' ? (
            <video src={attachment.url} controls style={styles.frame} />
          ) : previewKind === 'audio' ? (
            <div style={styles.unsupported}>
              <audio src={attachment.url} controls style={{ width: 'min(640px, 100%)' }} />
            </div>
          ) : (
            <div style={styles.unsupported}>
              <div style={{ color: BRAND_TEXT, fontWeight: 800 }}>Preview unavailable for this file type.</div>
              <div>Open the file in a new tab or download it to view it locally.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
